import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import StarRating from './StarRating';
import ReviewSummary from './ReviewSummary';
import { Review, ReviewListResponse, ReviewFilterRequest, ReviewTargetTypeValue } from '../../types/review';
import reviewService from '../../services/reviewService';
import { useNotification } from '../../hooks/useNotification';

const ReviewsContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  overflow: hidden;
`;

const FilterSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
`;

const FilterLabel = styled.span`
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #e0e0e0;
  padding: 8px 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  border-radius: 6px;
  min-width: 140px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.1);
  }

  &:hover {
    border-color: rgba(0, 212, 255, 0.4);
  }

  option {
    background: rgba(0, 20, 40, 0.95);
    color: #e0e0e0;
  }
`;

const ReviewItems = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const ReviewCard = styled.div`
  padding: 25px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 212, 255, 0.05);
    transform: translateY(-1px);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 12px;
`;

const ReviewerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserName = styled.span`
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
`;

const ReviewDate = styled.span`
  color: #a0a9b8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
`;

const ReviewText = styled.p`
  color: #e0e0e0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  margin: 15px 0;
  white-space: pre-wrap;
`;

const ReviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 15px;
  padding: 6px 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 11px;
  color: #00d4ff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: help;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
  }
`;

const UnverifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(160, 169, 184, 0.1);
  border: 1px solid rgba(160, 169, 184, 0.3);
  border-radius: 15px;
  padding: 6px 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 11px;
  color: #a0a9b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModerationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 15px;
  padding: 6px 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 11px;
  color: #ff6b6b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Pagination = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  background: ${props => props.active ? '#00d4ff' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.active ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'};
  color: ${props => props.active ? '#000000' : '#00d4ff'};
  padding: 8px 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  min-width: 40px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#00b8e6' : 'rgba(0, 212, 255, 0.1)'};
    border-color: #00d4ff;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #a0a9b8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  padding: 50px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #a0a9b8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  padding: 50px;
  line-height: 1.5;
`;

interface ReviewsListProps {
  targetType: ReviewTargetTypeValue;
  targetId: string;
  className?: string;
  showSummary?: boolean;
}

const ReviewsList: React.FC<ReviewsListProps> = ({
  targetType,
  targetId,
  className,
  showSummary = true
}) => {
  const [reviewData, setReviewData] = useState<ReviewListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReviewFilterRequest>({
    page: 1,
    pageSize: 10,
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const { showError } = useNotification();

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);

    try {
      let response: ReviewListResponse;

      switch (targetType) {
        case 'field':
          response = await reviewService.getFieldReviews(targetId, filter);
          break;
        case 'referee':
          response = await reviewService.getRefereeReviews(targetId, filter);
          break;
        case 'mission':
          response = await reviewService.getMissionReviews(targetId, filter);
          break;
        case 'game':
          response = await reviewService.getGameReviews(targetId, filter);
          break;
        default:
          throw new Error('Invalid target type');
      }

      setReviewData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load reviews');
      showError('Loading failed', 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [targetType, targetId, filter]);

  const handleFilterChange = (newFilter: Partial<ReviewFilterRequest>) => {
    setFilter(prev => ({ ...prev, ...newFilter, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilter(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !reviewData) {
    return (
      <ReviewsContainer className={className}>
        <LoadingMessage>Loading reviews...</LoadingMessage>
      </ReviewsContainer>
    );
  }

  if (error) {
    return (
      <ReviewsContainer className={className}>
        <LoadingMessage>Error: {error}</LoadingMessage>
      </ReviewsContainer>
    );
  }

  if (!reviewData) {
    return null;
  }

  const totalPages = Math.ceil(reviewData.totalCount / (filter.pageSize || 10));

  return (
    <ReviewsContainer className={className}>
      {showSummary && (
        <ReviewSummary 
          summary={reviewData.summary}
          loading={loading}
        />
      )}

      <FilterSection>
        <FilterLabel>Filter:</FilterLabel>
        
        <FilterSelect
          value={filter.isVerified?.toString() || 'all'}
          onChange={(e) => handleFilterChange({ 
            isVerified: e.target.value === 'all' ? undefined : e.target.value === 'true' 
          })}
        >
          <option value="all">All Reviews</option>
          <option value="true">Verified Only</option>
          <option value="false">Unverified Only</option>
        </FilterSelect>

        <FilterSelect
          value={filter.ratingFilter?.toString() || 'all'}
          onChange={(e) => handleFilterChange({ 
            ratingFilter: e.target.value === 'all' ? undefined : parseInt(e.target.value) 
          })}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4+ Stars</option>
          <option value="3">3+ Stars</option>
          <option value="2">2+ Stars</option>
          <option value="1">1+ Stars</option>
        </FilterSelect>

        <FilterSelect
          value={`${filter.sortBy || 'date'}_${filter.sortOrder || 'desc'}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split('_');
            handleFilterChange({ sortBy, sortOrder });
          }}
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="rating_desc">Highest Rating</option>
          <option value="rating_asc">Lowest Rating</option>
        </FilterSelect>
      </FilterSection>

      <ReviewItems>
        {reviewData.reviews.length === 0 ? (
          <EmptyMessage>
            No reviews match your filters. Try adjusting the filter options.
          </EmptyMessage>
        ) : (
          reviewData.reviews.map((review) => (
            <ReviewCard key={review.id}>
              <ReviewHeader>
                <ReviewerInfo>
                  <UserName>{review.userName}</UserName>
                  <StarRating rating={review.rating} readOnly size="small" />
                </ReviewerInfo>
                <ReviewDate>{formatDate(review.createdAt)}</ReviewDate>
              </ReviewHeader>

              {review.reviewText && (
                <ReviewText>{review.reviewText}</ReviewText>
              )}

              <ReviewFooter>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {review.isVerified ? (
                    <VerifiedBadge title={review.verificationDetails}>
                      ✓ Verified
                    </VerifiedBadge>
                  ) : (
                    <UnverifiedBadge>
                      Unverified
                    </UnverifiedBadge>
                  )}
                  
                  {!review.isConfirmed && (
                    <ModerationBadge>
                      Pending Moderation
                    </ModerationBadge>
                  )}
                </div>
              </ReviewFooter>
            </ReviewCard>
          ))
        )}
      </ReviewItems>

      {totalPages > 1 && (
        <Pagination>
          <PaginationButton
            onClick={() => handlePageChange(filter.page! - 1)}
            disabled={filter.page === 1}
          >
            ‹
          </PaginationButton>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, Math.min(totalPages - 4, filter.page! - 2)) + i;
            if (page > totalPages) return null;
            
            return (
              <PaginationButton
                key={page}
                active={page === filter.page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationButton>
            );
          })}

          <PaginationButton
            onClick={() => handlePageChange(filter.page! + 1)}
            disabled={filter.page === totalPages}
          >
            ›
          </PaginationButton>
        </Pagination>
      )}
    </ReviewsContainer>
  );
};

export default ReviewsList;